<template>
  <div>
    <div class="card p-2">
      <TitleButton
        :showBtn="true"
        title="Bill Payment Return"
        :showAddNew="false"
        @onClickCloseButton="navigateToListPage"
      />

      <div class="row gy-1 pt-75 bg-blue-light rounded-8 mt-1 pb-2">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Party</label>
          <input
            type="text"
            class="form-control invoice-edit-input"
            placeholder=""
            v-model="partyName"
            readonly
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Return Date</label>
          <input
            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
            tabindex="1"
            type="date"
            placeholder=""
            v-model="formData.date"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Payment Date</label>
          <input
            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
            tabindex="1"
            type="date"
            placeholder=""
            v-model="formData.payment_date"
            readonly
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Voucher No</label>
          <input
            type="text"
            class="form-control invoice-edit-input"
            placeholder=""
            v-model="formData.voucher_no"
            readonly
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-xl-3 col-form-label">Paid From</label>
          <v-select
            v-model="formData.account_head_id"
            placeholder="Select Cash & Bank Account"
            class="w-100"
            :options="accountHeads"
            label="name"
            :reduce="(name) => name.id"
            disabled
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Payment Ref</label>
          <div class="input-group input-group-merge invoice-edit-input-group">
            <div class="input-group-text">
              <i class="fa fa-hashtag"></i>
            </div>
            <input
              v-model="formData.payment_reference"
              type="text"
              class="form-control invoice-edit-input"
              placeholder=""
              readonly
            />
          </div>
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">P.Ref Date</label>
          <input
            class="form-control invoice-edit-input date-picker flatpickr-input flatpickr-mobile"
            tabindex="1"
            type="date"
            placeholder=""
            v-model="formData.payment_reference_date"
            readonly
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Return Amount</label>
          <div class="input-group">
            <input
              v-model="formData.total_paid_amount"
              type="number"
              class="form-control"
              placeholder=""
              readonly
            />
          </div>
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">VDS Payable</label>
          <v-select
            v-model="formData.vds_payable_account_head_id"
            placeholder="Select VDS Payable"
            class="w-100"
            :options="vdsPayableHeads"
            label="name"
            :reduce="(name) => name.id"
            disabled
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">TDS Payable</label>
          <v-select
            v-model="formData.tds_payable_account_head_id"
            placeholder="Select TDS Payable"
            class="w-100"
            :options="tdsPayableHeads"
            label="name"
            :reduce="(name) => name.id"
            disabled
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Discount Receipt</label>
          <v-select
            v-model="formData.discount_receipt_head_id"
            placeholder="Select Discount Receipt"
            class="w-100"
            :options="discountAccountHeads"
            label="name"
            :reduce="(name) => name.id"
            disabled
          />
        </div>
      </div>

      <div class="mt-2"></div>
      <BillPayTable :payBills="dueBills" :isDisabled="true" />

      <div class="row mt-1">
        <div class="col-12">
          <div class="mb-2">
            <label for="note" class="form-label fw-bold">Memo</label>
            <textarea
              v-model="formData.description"
              placeholder="Memo"
              class="form-control"
              rows="2"
              id="note"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="d-flex gap-1">
        <button
          :disabled="saveButtonLoader"
          @click="handleReturn"
          class="btn btn-primary"
        >
          Return
        </button>
        <button
          @click="navigateToListPage"
          class="btn btn-outline-secondary cancel-btn"
        >
          Cancel
        </button>
      </div>
    </div>
    <GlobalLoader />
  </div>
</template>

<script setup>
import { ref, inject, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import TitleButton from "@/components/atom/TitleButton.vue";
import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import BillPayTable from "@/components/molecule/company/inventory/purchase/BillPayTable.vue";
import useBillPayData from "@/data/inventory/billPay.js";
import handlePurchaseAndSales from "@/services/modules/purchase";
import handlePurchase from "@/services/modules/purchase";
import handlePayBill from "@/services/modules/payBill";
import useDate from "@/services/utils/day";

const route = useRoute();
const router = useRouter();
const date = useDate();
const voucherQuery = `?company_id=${route.params.companyId}&voucher_type=payment_voucher&txn_type=bill_payment`;

const { formData, fetchSinglePayment } = handlePurchaseAndSales();
const { getAccountHeadBySlag } = handlePurchase();
const { returnBillPayment } = handlePayBill();
const { getAccountHead, accountHeads, dueBills, params } = useBillPayData(voucherQuery);

const showError = inject("showError");
const showSuccess = inject("showSuccess");
const saveButtonLoader = ref(false);
const partyName = ref("");
const vdsPayableHeads = ref([]);
const tdsPayableHeads = ref([]);
const discountAccountHeads = ref([]);

const navigateToListPage = () => {
  router.push({ name: "pay-bill-list", params: params, query: route.query });
}

const handleReturn = () => {
  saveButtonLoader.value = true;
  const payload = {
    company_id: params.companyId,
    date: formData.value.date,
    description: formData.value.description
  };
  returnBillPayment(route.params.paymentId, payload)
    .then((res) => {
      saveButtonLoader.value = false;
      if (!res.status) {
        return showError(res.message);
      }
      showSuccess(res.message);
      navigateToListPage();
    })
    .catch(() => {
      saveButtonLoader.value = false;
    });
}

const setPartyName = (res) => {
  if (!res.data.general || res.data.general.length < 1) return
  const firstBill = res.data.general[0];
  if (!firstBill.supplier) return
  partyName.value = firstBill.supplier.name;
}

onMounted(async () => {
  const companyQuery = `?company_id=${params.companyId}`;
  getAccountHead();

  await fetchSinglePayment(route.params.paymentId, companyQuery).then((res) => {
    if (!res.status) {
      return showError(res.message);
    }
    formData.value = res.data;
    dueBills.value = res.data.general;
    setPartyName(res);
  });

  formData.value.date = date.currentDate();

  const vdsPayableHeadsRes = getAccountHeadBySlag("vds_payable", companyQuery);
  const tdsPayableHeadsRes = getAccountHeadBySlag("tds_payable", companyQuery);
  const discountAccountHeadsRes = getAccountHeadBySlag("discount_receipt", companyQuery);
  Promise.all([
    vdsPayableHeadsRes.then(({ data }) => {
      if (data) vdsPayableHeads.value = data
    }),
    tdsPayableHeadsRes.then(({ data }) => {
      if (data) tdsPayableHeads.value = data
    }),
    discountAccountHeadsRes.then(({ data }) => {
      if (data) discountAccountHeads.value = data
    })
  ]);
});
</script>
